const teacherAdmin = {
  en: {
    login: {
      title: 'Teacher portal',
      subtitle: 'Sign in to your account',
      email: 'Email',
      noAccount: "Don't have an account? Contact your mentor for an invitation.",
      loginFailed: 'Login failed. Please check your credentials.',
      emailOnlyError: 'Teacher sign-in uses your email address, not a phone number. Enter the email your invitation was sent to.',
      tagline1: 'Teach with clarity,',
      taglineEm: 'reach every student',
      desc: 'Your teacher workspace — manage lessons, track student progress, and stay connected, all in one place.',
      feature1Title: 'Your courses',
      feature1Desc: 'Manage lessons, assignments and quizzes for the courses you teach',
      feature2Title: 'Student progress',
      feature2Desc: 'Track completion, grades and engagement for every student',
      feature3Title: 'Direct messaging',
      feature3Desc: 'Answer questions and stay connected with your students',
    },
    invite: {
      pageTitle: 'Invite teacher',
      pageTitleWithCourse: 'Invite teacher to {course}',
      viewAllTeachers: 'View all teachers',
      courseNotFound: 'Course not found',
      courseLoadFailedTitle: "Couldn't load this course",
      courseLoadFailedBody: 'Something went wrong while loading the course. Check your connection and try again.',
      backToCourses: 'Back to courses',
      sendInvitation: 'Send invitation',
      emailAddress: 'Email address',
      emailPlaceholder: 'teacher@example.com',
      phoneNumber: 'Phone number',
      phonePlaceholder: '+998 XX XXX XX XX',
      sendingInvitation: 'Sending invitation...',
      invitationInfo: 'The teacher will receive an invitation link valid for 48 hours.',
      showPending: 'Show pending invitations ({count})',
      hidePending: 'Hide pending invitations ({count})',
      noPendingInvitations: 'No pending invitations',
      emailPhoneHeader: 'Email / phone',
      sentDateHeader: 'Sent date',
      expiresHeader: 'Expires',
      errors: {
        missingContact: 'Please enter either an email or phone number',
        loadCourseFailed: 'Failed to load course',
        sendFailed: 'Failed to send invitation',
      },
      retry: 'Try again',
      successTitle: 'Invitation sent',
      successBody: '{contact} will get a link to set a password and start teaching {course}. It expires in 48 hours.',
      linkLabel: 'Invitation link',
      linkHint: "Share this if the invite email or SMS doesn't arrive.",
      copyLink: 'Copy link',
      linkCopied: 'Copied',
      sendAnother: 'Send another invitation',
    },
    accept: {
      invalidTitle: 'Invitation invalid',
      invalidBody: 'This invitation link is invalid or incomplete.',
      expiredTitle: 'Invitation expired',
      expiredBody: 'This invitation has expired or is no longer valid. Please ask your mentor for a new link.',
      alreadyAcceptedTitle: 'Invitation already used',
      alreadyAcceptedBody: 'This invitation has already been accepted. Sign in to continue.',
      networkTitle: 'Connection issue',
      networkBody: 'We could not reach the server. Check your internet connection and try again.',
      genericTitle: 'Invitation error',
      genericBody: 'We could not complete the invitation acceptance. Please try again or contact support.',
      badge: 'Teacher invitation',
      tagline1: 'One invitation,',
      taglineEm: 'three steps to teach',
      desc: 'Set a password, get access to your course, and start guiding students — all in a couple of minutes.',
      step1Title: 'Create your password',
      step1Desc: 'Secure your teacher account with a password only you know',
      step2Title: 'Get course access',
      step2Desc: "You'll be added to the course you were invited to teach",
      step3Title: 'Start teaching',
      step3Desc: 'Track progress, message students and manage lessons',
      pageTitle: 'Accept teacher invitation',
      subtitle: 'You were invited to join Darsly as a teacher. Complete your details to continue.',
      fullName: 'Full name',
      fullNamePlaceholder: 'Enter your full name...',
      password: 'Password',
      passwordPlaceholder: 'Create a password...',
      passwordHint: 'At least 8 characters, with a letter and a number',
      confirmPassword: 'Confirm password',
      confirmPasswordPlaceholder: 'Re-enter your password...',
      submitting: 'Accepting invitation...',
      submit: 'Accept invitation',
      support: 'Support: {email}',
      backToLogin: 'Go to teacher login',
      goToDashboard: 'Go to dashboard',
      retry: 'Retry',
      agreement: 'By accepting, you agree to the teacher agreement and code of conduct.',
      successTitle: 'Invitation accepted',
      successFallbackBody: 'You have joined Darsly as a teacher. Redirecting you to the dashboard now.',
      successWithCourseBody: 'You have joined {course} as a teacher. Redirecting you to the dashboard now.',
      successButton: 'Open teacher dashboard',
      successCountdown: 'Redirecting automatically in a moment.',
      errors: {
        nameRequired: 'Please enter your full name',
        passwordRequired: 'Please create a password',
        passwordWeak: 'Password must be at least 8 characters and include a letter and a number',
        passwordMismatch: 'Passwords do not match',
      },
    },
    assign: {
      pageTitle: 'Assign students',
      teacherNotFound: 'Teacher not found',
      loadFailed: 'Failed to load data',
      retry: 'Try again',
      heading: 'Assign students to {teacher} — {course}',
      teacherFallback: 'Teacher',
      backToTeachers: 'Back to teachers',
      searchPlaceholder: 'Search student name, email or phone',
      assigning: 'Assigning...',
      assignButton: 'Assign {count} student(s)',
      selectAtLeastOne: 'Please select at least one student',
      assignFailed: 'Failed to assign students',
      assignSuccess: 'Successfully assigned {assigned} student(s).',
      alreadyAssignedSuffix: '{skipped} student(s) already assigned.',
      nameHeader: 'Name',
      emailHeader: 'Email / phone',
      progressHeader: 'Progress',
      enrolledDateHeader: 'Enrolled date',
      inactiveBadge: 'No access',
      inactiveHint: 'Refunded, revoked or expired enrollment: this student cannot be assigned.',
      notAssignedTitle: '{count} student(s) were not assigned:',
      reasonNotEnrolled: 'Not enrolled in this course, or the enrollment is inactive',
      showingCount: 'Showing {shown} of {total}',
      loadMore: 'Load more students',
      loadingMore: 'Loading…',
      noMatch: 'No students match your search',
      noMatchAction: 'Clear search',
      noneEnrolled: 'No students enrolled in this course',
      noneEnrolledAction: 'View course enrollments',
      selectAllStudents: 'Select all students',
      selectStudent: 'Select {name}',
    },
    activity: {
      pageTitle: 'Teachers activity',
      pageTitleWithCourse: 'Teacher activity — {course}',
      inviteTeacher: '+ Invite teacher',
      backToCourses: 'Back to courses',
      loadFailed: 'Failed to load data',
      retry: 'Try again',
      totalTeachers: 'Total teachers',
      studentsAssigned: 'Students assigned',
      messagesLast7Days: 'Messages (last 7 days)',
      noTeachersTitle: 'No teachers yet',
      noTeachersBody: 'Invite teachers to help manage this course.',
      inviteFirstTeacher: 'Invite first teacher',
      nameHeader: 'Name',
      contactHeader: 'Contact',
      studentsAssignedHeader: 'Students assigned',
      messages7dHeader: 'Messages (7d)',
      lastActiveHeader: 'Last active',
      actionsHeader: 'Actions',
      assignStudents: 'Assign students',
      remove: 'Remove',
      removeSuccess: '{name} has been removed from the course',
      removeFailed: 'Failed to remove teacher',
      removeModalTitle: 'Remove teacher',
      removeModalBodyPrefix: 'Are you sure you want to remove ',
      removeModalBodySuffix: ' from this course? All student assignments will be deleted.',
      cancel: 'Cancel',
      removing: 'Removing...',
      confirmRemove: 'Remove teacher',
    },
    list: {
      pageTitle: 'Course teachers',
      pageTitleWithCourse: 'Course teachers — {course}',
      inviteTeacher: '+ Invite teacher',
      loadFailed: 'Failed to load teachers',
      retry: 'Try again',
      searchPlaceholder: 'Search teacher name, email or phone',
      resultCount: '{filtered} of {total} teachers',
      noMatchTitle: 'No teachers match your search',
      clearSearch: 'Clear search',
      noTeachersTitle: 'No teachers yet',
      inviteFirstTeacher: 'Invite first teacher',
      students: 'Students',
      messages: 'Messages',
      lastActive: 'Last active: {date}',
      assignStudents: 'Assign students',
      remove: 'Remove',
      removeModalTitle: 'Remove teacher',
      removeModalBodyPrefix: 'Are you sure you want to remove ',
      removeModalBodySuffix: ' from this course? All student assignments will be deleted.',
      cancel: 'Cancel',
      removing: 'Removing...',
      confirmRemove: 'Remove teacher',
      removeFailed: 'Failed to remove teacher',
    },
  },
  ru: {
    login: {
      title: 'Портал преподавателя',
      subtitle: 'Войдите в свой аккаунт',
      email: 'Email',
      noAccount: 'Нет аккаунта? Обратитесь к своему куратору за приглашением.',
      loginFailed: 'Не удалось войти. Проверьте данные для входа.',
      emailOnlyError: 'Вход для преподавателей выполняется по email, а не по номеру телефона. Введите email, на который пришло приглашение.',
      tagline1: 'Преподавайте с ясностью,',
      taglineEm: 'достигайте каждого студента',
      desc: 'Ваше рабочее пространство преподавателя — управляйте уроками, отслеживайте прогресс студентов и оставайтесь на связи, всё в одном месте.',
      feature1Title: 'Ваши курсы',
      feature1Desc: 'Управляйте уроками, заданиями и тестами по своим курсам',
      feature2Title: 'Прогресс студентов',
      feature2Desc: 'Отслеживайте успеваемость, оценки и вовлечённость каждого студента',
      feature3Title: 'Личные сообщения',
      feature3Desc: 'Отвечайте на вопросы и оставайтесь на связи со студентами',
    },
    invite: {
      pageTitle: 'Пригласить преподавателя',
      pageTitleWithCourse: 'Пригласить преподавателя в курс {course}',
      viewAllTeachers: 'Все преподаватели',
      courseNotFound: 'Курс не найден',
      courseLoadFailedTitle: 'Не удалось загрузить курс',
      courseLoadFailedBody: 'Что-то пошло не так при загрузке курса. Проверьте соединение и попробуйте снова.',
      backToCourses: 'Назад к курсам',
      sendInvitation: 'Отправить приглашение',
      emailAddress: 'Email адрес',
      emailPlaceholder: 'teacher@example.com',
      phoneNumber: 'Номер телефона',
      phonePlaceholder: '+998 XX XXX XX XX',
      sendingInvitation: 'Отправка приглашения...',
      invitationInfo: 'Преподаватель получит ссылку-приглашение, действительную 48 часов.',
      showPending: 'Показать ожидающие приглашения ({count})',
      hidePending: 'Скрыть ожидающие приглашения ({count})',
      noPendingInvitations: 'Нет ожидающих приглашений',
      emailPhoneHeader: 'Email / телефон',
      sentDateHeader: 'Дата отправки',
      expiresHeader: 'Истекает',
      errors: {
        missingContact: 'Введите email или номер телефона',
        loadCourseFailed: 'Не удалось загрузить курс',
        sendFailed: 'Не удалось отправить приглашение',
      },
      retry: 'Повторить',
      successTitle: 'Приглашение отправлено',
      successBody: '{contact} получит ссылку для установки пароля и начала преподавания в курсе {course}. Ссылка действительна 48 часов.',
      linkLabel: 'Ссылка-приглашение',
      linkHint: 'Поделитесь ею, если письмо или SMS не дойдут.',
      copyLink: 'Скопировать ссылку',
      linkCopied: 'Скопировано',
      sendAnother: 'Отправить ещё одно приглашение',
    },
    accept: {
      invalidTitle: 'Недействительное приглашение',
      invalidBody: 'Ссылка-приглашение недействительна или неполная.',
      expiredTitle: 'Приглашение истекло',
      expiredBody: 'Срок действия этого приглашения истёк или оно больше недействительно. Попросите у куратора новую ссылку.',
      alreadyAcceptedTitle: 'Приглашение уже использовано',
      alreadyAcceptedBody: 'Это приглашение уже принято. Войдите в систему, чтобы продолжить.',
      networkTitle: 'Проблема с подключением',
      networkBody: 'Не удалось связаться с сервером. Проверьте подключение к интернету и попробуйте снова.',
      genericTitle: 'Ошибка приглашения',
      genericBody: 'Не удалось завершить принятие приглашения. Попробуйте снова или обратитесь в поддержку.',
      badge: 'Приглашение преподавателя',
      tagline1: 'Одно приглашение —',
      taglineEm: 'три шага до старта',
      desc: 'Задайте пароль, получите доступ к курсу и начните направлять студентов — всё за пару минут.',
      step1Title: 'Создайте пароль',
      step1Desc: 'Защитите аккаунт преподавателя паролем, который знаете только вы',
      step2Title: 'Доступ к курсу',
      step2Desc: 'Вас добавят к курсу, на который вас пригласили',
      step3Title: 'Начните преподавать',
      step3Desc: 'Отслеживайте прогресс, переписывайтесь со студентами и управляйте уроками',
      pageTitle: 'Принять приглашение преподавателя',
      subtitle: 'Вас пригласили присоединиться к Darsly в качестве преподавателя. Заполните данные, чтобы продолжить.',
      fullName: 'Полное имя',
      fullNamePlaceholder: 'Введите ваше полное имя...',
      password: 'Пароль',
      passwordPlaceholder: 'Придумайте пароль...',
      passwordHint: 'Не менее 8 символов, с буквой и цифрой',
      confirmPassword: 'Повторите пароль',
      confirmPasswordPlaceholder: 'Введите пароль ещё раз...',
      submitting: 'Принятие приглашения...',
      submit: 'Принять приглашение',
      support: 'Поддержка: {email}',
      backToLogin: 'Перейти к входу для преподавателей',
      goToDashboard: 'Перейти на панель управления',
      retry: 'Повторить',
      agreement: 'Принимая приглашение, вы соглашаетесь с договором преподавателя и кодексом поведения.',
      successTitle: 'Приглашение принято',
      successFallbackBody: 'Вы присоединились к Darsly в качестве преподавателя. Сейчас вы будете перенаправлены на панель управления.',
      successWithCourseBody: 'Вы присоединились к курсу {course} в качестве преподавателя. Сейчас вы будете перенаправлены на панель управления.',
      successButton: 'Открыть панель преподавателя',
      successCountdown: 'Автоматическое перенаправление через мгновение.',
      errors: {
        nameRequired: 'Введите ваше полное имя',
        passwordRequired: 'Придумайте пароль',
        passwordWeak: 'Пароль должен содержать не менее 8 символов, включая букву и цифру',
        passwordMismatch: 'Пароли не совпадают',
      },
    },
    assign: {
      pageTitle: 'Назначить студентов',
      teacherNotFound: 'Преподаватель не найден',
      loadFailed: 'Не удалось загрузить данные',
      retry: 'Повторить',
      heading: 'Назначить студентов для {teacher} — {course}',
      teacherFallback: 'Преподаватель',
      backToTeachers: 'Назад к преподавателям',
      searchPlaceholder: 'Поиск по имени, email или телефону студента',
      assigning: 'Назначение...',
      assignButton: 'Назначить {count} студент(ов)',
      selectAtLeastOne: 'Выберите хотя бы одного студента',
      assignFailed: 'Не удалось назначить студентов',
      assignSuccess: 'Успешно назначено {assigned} студент(ов).',
      alreadyAssignedSuffix: '{skipped} студент(ов) уже назначено.',
      nameHeader: 'Имя',
      emailHeader: 'Email / телефон',
      progressHeader: 'Прогресс',
      enrolledDateHeader: 'Дата зачисления',
      inactiveBadge: 'Нет доступа',
      inactiveHint: 'Возврат, отзыв или истёкший доступ: этого студента нельзя назначить.',
      notAssignedTitle: 'Не назначено студентов: {count}',
      reasonNotEnrolled: 'Не записан на этот курс или запись неактивна',
      showingCount: 'Показано {shown} из {total}',
      loadMore: 'Загрузить ещё студентов',
      loadingMore: 'Загрузка…',
      noMatch: 'Нет студентов, соответствующих поиску',
      noMatchAction: 'Очистить поиск',
      noneEnrolled: 'На этот курс не зачислены студенты',
      noneEnrolledAction: 'К зачислениям курса',
      selectAllStudents: 'Выбрать всех студентов',
      selectStudent: 'Выбрать {name}',
    },
    activity: {
      pageTitle: 'Активность преподавателей',
      pageTitleWithCourse: 'Активность преподавателей — {course}',
      inviteTeacher: '+ Пригласить преподавателя',
      backToCourses: 'Назад к курсам',
      loadFailed: 'Не удалось загрузить данные',
      retry: 'Повторить',
      totalTeachers: 'Всего преподавателей',
      studentsAssigned: 'Назначено студентов',
      messagesLast7Days: 'Сообщений (за 7 дней)',
      noTeachersTitle: 'Пока нет преподавателей',
      noTeachersBody: 'Пригласите преподавателей для управления этим курсом.',
      inviteFirstTeacher: 'Пригласить первого преподавателя',
      nameHeader: 'Имя',
      contactHeader: 'Контакты',
      studentsAssignedHeader: 'Назначено студентов',
      messages7dHeader: 'Сообщений (7д)',
      lastActiveHeader: 'Последняя активность',
      actionsHeader: 'Действия',
      assignStudents: 'Назначить студентов',
      remove: 'Удалить',
      removeSuccess: '{name} удалён(а) из курса',
      removeFailed: 'Не удалось удалить преподавателя',
      removeModalTitle: 'Удалить преподавателя',
      removeModalBodyPrefix: 'Вы уверены, что хотите удалить ',
      removeModalBodySuffix: ' из этого курса? Все назначения студентов будут удалены.',
      cancel: 'Отмена',
      removing: 'Удаление...',
      confirmRemove: 'Удалить преподавателя',
    },
    list: {
      pageTitle: 'Преподаватели курса',
      pageTitleWithCourse: 'Преподаватели курса — {course}',
      inviteTeacher: '+ Пригласить преподавателя',
      loadFailed: 'Не удалось загрузить преподавателей',
      retry: 'Повторить',
      searchPlaceholder: 'Поиск по имени, email или телефону преподавателя',
      resultCount: '{filtered} из {total} преподавателей',
      noMatchTitle: 'Нет преподавателей, соответствующих поиску',
      clearSearch: 'Очистить поиск',
      noTeachersTitle: 'Пока нет преподавателей',
      inviteFirstTeacher: 'Пригласить первого преподавателя',
      students: 'Студенты',
      messages: 'Сообщения',
      lastActive: 'Последняя активность: {date}',
      assignStudents: 'Назначить студентов',
      remove: 'Удалить',
      removeModalTitle: 'Удалить преподавателя',
      removeModalBodyPrefix: 'Вы уверены, что хотите удалить ',
      removeModalBodySuffix: ' из этого курса? Все назначения студентов будут удалены.',
      cancel: 'Отмена',
      removing: 'Удаление...',
      confirmRemove: 'Удалить преподавателя',
      removeFailed: 'Не удалось удалить преподавателя',
    },
  },
  uz: {
    login: {
      title: "O'qituvchi portali",
      subtitle: 'Hisobingizga kiring',
      email: 'Email',
      noAccount: "Hisobingiz yo'qmi? Taklif olish uchun mentoringizga murojaat qiling.",
      loginFailed: "Kirish amalga oshmadi. Ma'lumotlaringizni tekshiring.",
      emailOnlyError: "O'qituvchi tizimga email orqali kiradi, telefon raqami orqali emas. Taklif yuborilgan email manzilini kiriting.",
      tagline1: 'Aniq dars bering,',
      taglineEm: "har bir o'quvchiga yeting",
      desc: "Sizning o'qituvchi ish maydoningiz — darslarni boshqaring, o'quvchilar progressini kuzating va aloqada bo'ling, barchasi bir joyda.",
      feature1Title: 'Sizning kurslaringiz',
      feature1Desc: "O'zingiz o'qitadigan kurslar uchun darslar, topshiriqlar va testlarni boshqaring",
      feature2Title: "O'quvchilar progressi",
      feature2Desc: "Har bir o'quvchining bajarilishi, baholari va faolligini kuzating",
      feature3Title: "To'g'ridan-to'g'ri xabarlar",
      feature3Desc: "Savollarga javob bering va o'quvchilar bilan aloqada bo'ling",
    },
    invite: {
      pageTitle: "O'qituvchini taklif qilish",
      pageTitleWithCourse: "{course} kursiga o'qituvchi taklif qilish",
      viewAllTeachers: "Barcha o'qituvchilar",
      courseNotFound: 'Kurs topilmadi',
      courseLoadFailedTitle: "Kursni yuklab bo'lmadi",
      courseLoadFailedBody: "Kursni yuklashda xatolik yuz berdi. Internet aloqasini tekshirib, qayta urinib ko'ring.",
      backToCourses: 'Kurslarga qaytish',
      sendInvitation: 'Taklif yuborish',
      emailAddress: 'Email manzil',
      emailPlaceholder: 'teacher@example.com',
      phoneNumber: 'Telefon raqami',
      phonePlaceholder: '+998 XX XXX XX XX',
      sendingInvitation: 'Taklif yuborilmoqda...',
      invitationInfo: "O'qituvchi 48 soat amal qiladigan taklif havolasini oladi.",
      showPending: "Kutilayotgan takliflarni ko'rsatish ({count})",
      hidePending: 'Kutilayotgan takliflarni yashirish ({count})',
      noPendingInvitations: "Kutilayotgan takliflar yo'q",
      emailPhoneHeader: 'Email / telefon',
      sentDateHeader: 'Yuborilgan sana',
      expiresHeader: 'Amal qilish muddati',
      errors: {
        missingContact: 'Email yoki telefon raqamini kiriting',
        loadCourseFailed: "Kursni yuklab bo'lmadi",
        sendFailed: "Taklifni yuborib bo'lmadi",
      },
      retry: 'Qayta urinish',
      successTitle: 'Taklif yuborildi',
      successBody: "{contact} parol o'rnatish va {course} kursida dars berishni boshlash uchun havola oladi. Havola 48 soat amal qiladi.",
      linkLabel: 'Taklif havolasi',
      linkHint: 'Agar email yoki SMS yetib bormasa, shu havolani ulashing.',
      copyLink: 'Havolani nusxalash',
      linkCopied: 'Nusxalandi',
      sendAnother: 'Yana bitta taklif yuborish',
    },
    accept: {
      invalidTitle: 'Taklif yaroqsiz',
      invalidBody: "Ushbu taklif havolasi noto'g'ri yoki to'liq emas.",
      expiredTitle: 'Taklif muddati tugagan',
      expiredBody: "Ushbu taklif muddati tugagan yoki endi yaroqli emas. Mentoringizdan yangi havola so'rang.",
      alreadyAcceptedTitle: 'Taklif allaqachon ishlatilgan',
      alreadyAcceptedBody: 'Ushbu taklif allaqachon qabul qilingan. Davom etish uchun tizimga kiring.',
      networkTitle: 'Ulanish muammosi',
      networkBody: "Serverga ulanib bo'lmadi. Internet aloqangizni tekshirib, qayta urinib ko'ring.",
      genericTitle: 'Taklif xatosi',
      genericBody: "Taklifni qabul qilishni yakunlab bo'lmadi. Qayta urinib ko'ring yoki yordamga murojaat qiling.",
      badge: "O'qituvchi taklifi",
      tagline1: 'Bitta taklif —',
      taglineEm: 'uch qadamda dars berish',
      desc: "Parol o'rnating, kursga kirish huquqini oling va o'quvchilarga yo'l-yo'riq ko'rsatishni boshlang — bor-yo'g'i bir necha daqiqada.",
      step1Title: 'Parol yarating',
      step1Desc: "Faqat sizga ma'lum parol bilan o'qituvchi hisobingizni himoya qiling",
      step2Title: 'Kursga kirish',
      step2Desc: "Siz taklif qilingan kursga qo'shilasiz",
      step3Title: 'Dars berishni boshlang',
      step3Desc: "Progressni kuzating, o'quvchilar bilan yozishing va darslarni boshqaring",
      pageTitle: "O'qituvchi taklifini qabul qilish",
      subtitle: "Siz Darsly platformasiga o'qituvchi sifatida taklif qilindingiz. Davom etish uchun ma'lumotlaringizni kiriting.",
      fullName: "To'liq ism",
      fullNamePlaceholder: "To'liq ismingizni kiriting...",
      password: 'Parol',
      passwordPlaceholder: "Parol o'ylab toping...",
      passwordHint: "Kamida 8 ta belgi, harf va raqamdan iborat bo'lsin",
      confirmPassword: 'Parolni tasdiqlang',
      confirmPasswordPlaceholder: 'Parolni qayta kiriting...',
      submitting: 'Taklif qabul qilinmoqda...',
      submit: 'Taklifni qabul qilish',
      support: 'Yordam: {email}',
      backToLogin: "O'qituvchi portaliga o'tish",
      goToDashboard: "Bosh sahifaga o'tish",
      retry: 'Qayta urinish',
      agreement: "Qabul qilish orqali siz o'qituvchi kelishuvi va odob qoidalariga rozilik bildirasiz.",
      successTitle: 'Taklif qabul qilindi',
      successFallbackBody: "Siz Darsly platformasiga o'qituvchi sifatida qo'shildingiz. Hozir bosh sahifaga yo'naltirilasiz.",
      successWithCourseBody: "Siz {course} kursiga o'qituvchi sifatida qo'shildingiz. Hozir bosh sahifaga yo'naltirilasiz.",
      successButton: "O'qituvchi bosh sahifasini ochish",
      successCountdown: "Bir ozdan so'ng avtomatik yo'naltirilasiz.",
      errors: {
        nameRequired: "To'liq ismingizni kiriting",
        passwordRequired: "Parol o'ylab toping",
        passwordWeak: "Parol kamida 8 ta belgidan, harf va raqamdan iborat bo'lishi kerak",
        passwordMismatch: 'Parollar mos kelmadi',
      },
    },
    assign: {
      pageTitle: "O'quvchilarni biriktirish",
      teacherNotFound: "O'qituvchi topilmadi",
      loadFailed: "Ma'lumotlarni yuklab bo'lmadi",
      retry: 'Qayta urinish',
      heading: "{teacher} uchun o'quvchilarni biriktirish — {course}",
      teacherFallback: "O'qituvchi",
      backToTeachers: "O'qituvchilarga qaytish",
      searchPlaceholder: "O'quvchi ismi, emaili yoki telefonini qidirish",
      assigning: 'Biriktirilmoqda...',
      assignButton: "{count} ta o'quvchini biriktirish",
      selectAtLeastOne: "Kamida bitta o'quvchini tanlang",
      assignFailed: "O'quvchilarni biriktirib bo'lmadi",
      assignSuccess: "{assigned} ta o'quvchi muvaffaqiyatli biriktirildi.",
      alreadyAssignedSuffix: "{skipped} ta o'quvchi allaqachon biriktirilgan.",
      nameHeader: 'Ism',
      emailHeader: 'Email / telefon',
      progressHeader: 'Progress',
      enrolledDateHeader: "Ro'yxatdan o'tgan sana",
      inactiveBadge: 'Kirish yopilgan',
      inactiveHint: "Qaytarilgan, bekor qilingan yoki muddati tugagan yozilish: bu o'quvchini biriktirib bo'lmaydi.",
      notAssignedTitle: "{count} ta o'quvchi biriktirilmadi:",
      reasonNotEnrolled: 'Bu kursga yozilmagan yoki yozilish faol emas',
      showingCount: "{total} tadan {shown} tasi ko'rsatilmoqda",
      loadMore: "Yana o'quvchilarni yuklash",
      loadingMore: 'Yuklanmoqda…',
      noMatch: "Qidiruvga mos o'quvchi topilmadi",
      noMatchAction: 'Qidiruvni tozalash',
      noneEnrolled: "Bu kursga o'quvchilar ro'yxatdan o'tmagan",
      noneEnrolledAction: "Kurs ro'yxatga olishlarini ko'rish",
      selectAllStudents: "Barcha o'quvchilarni tanlash",
      selectStudent: '{name} ni tanlash',
    },
    activity: {
      pageTitle: "O'qituvchilar faoliyati",
      pageTitleWithCourse: "O'qituvchilar faoliyati — {course}",
      inviteTeacher: "+ O'qituvchi taklif qilish",
      backToCourses: 'Kurslarga qaytish',
      loadFailed: "Ma'lumotlarni yuklab bo'lmadi",
      retry: 'Qayta urinish',
      totalTeachers: "Jami o'qituvchilar",
      studentsAssigned: "Biriktirilgan o'quvchilar",
      messagesLast7Days: 'Xabarlar (oxirgi 7 kun)',
      noTeachersTitle: "Hozircha o'qituvchilar yo'q",
      noTeachersBody: "Ushbu kursni boshqarishga yordam berish uchun o'qituvchilarni taklif qiling.",
      inviteFirstTeacher: "Birinchi o'qituvchini taklif qilish",
      nameHeader: 'Ism',
      contactHeader: 'Aloqa',
      studentsAssignedHeader: "Biriktirilgan o'quvchilar",
      messages7dHeader: 'Xabarlar (7k)',
      lastActiveHeader: 'Oxirgi faollik',
      actionsHeader: 'Amallar',
      assignStudents: "O'quvchilarni biriktirish",
      remove: "O'chirish",
      removeSuccess: '{name} kursdan olib tashlandi',
      removeFailed: "O'qituvchini o'chirib bo'lmadi",
      removeModalTitle: "O'qituvchini o'chirish",
      removeModalBodyPrefix: 'Haqiqatan ham ',
      removeModalBodySuffix: " ni ushbu kursdan olib tashlamoqchimisiz? Barcha o'quvchi biriktirmalari o'chiriladi.",
      cancel: 'Bekor qilish',
      removing: "O'chirilmoqda...",
      confirmRemove: "O'qituvchini o'chirish",
    },
    list: {
      pageTitle: "Kurs o'qituvchilari",
      pageTitleWithCourse: "Kurs o'qituvchilari — {course}",
      inviteTeacher: "+ O'qituvchi taklif qilish",
      loadFailed: "O'qituvchilarni yuklab bo'lmadi",
      retry: 'Qayta urinish',
      searchPlaceholder: "O'qituvchi ismi, email yoki telefonini qidirish",
      resultCount: "{total} tadan {filtered} ta o'qituvchi",
      noMatchTitle: "Qidiruvga mos o'qituvchi topilmadi",
      clearSearch: 'Qidiruvni tozalash',
      noTeachersTitle: "Hozircha o'qituvchilar yo'q",
      inviteFirstTeacher: "Birinchi o'qituvchini taklif qilish",
      students: "O'quvchilar",
      messages: 'Xabarlar',
      lastActive: 'Oxirgi faollik: {date}',
      assignStudents: "O'quvchilarni biriktirish",
      remove: "O'chirish",
      removeModalTitle: "O'qituvchini o'chirish",
      removeModalBodyPrefix: 'Haqiqatan ham ',
      removeModalBodySuffix: " ni ushbu kursdan olib tashlamoqchimisiz? Barcha o'quvchi biriktirmalari o'chiriladi.",
      cancel: 'Bekor qilish',
      removing: "O'chirilmoqda...",
      confirmRemove: "O'qituvchini o'chirish",
      removeFailed: "O'qituvchini o'chirib bo'lmadi",
    },
  },
};

export default teacherAdmin;
